import Backbone from 'backbone';
import Promise from 'bluebird';
import _ from 'underscore';

// This version which is MIT license is not in npm registry yet, so here check in source code.
import '../lib/jquery-jcrop/js/Jcrop';

import template from './image-cropper-template.pug';


export class ImageCropper extends Backbone.View {
  /**
   *
   * @param {object} cropperOptions the settings for the ImageCropper.
   *   See detail of most common fields below. Link for the documentation is provided, for further
   *   information.
   * @param {number} [cropperOptions.aspectRatio=1] Aspect ratio of the cropped area. If not
   * passed. Only values greater than zero.
   * @param {array} cropperOptions.maxSize array containing the maximum width and height of
   * for the selector, [w,h].
   * @param {array} cropperOptions.minSize array containing the minimum width and height of
   * for the selector, [w,h].
   * @param {number} [cropperOptions.bgOpacity=0.6] opacity value for the background shading of
   * the cropper. [0, 1] decimal values (0: opaque, 1: transparent).
   * @param {string} [cropperOptions.bgColor='black'] (e.g. 'red', '#ccc', 'rgb(10,10,10)') color
   * value for the background shading of the cropper.
   * @param {string} [cropperOptions.canvasBgColor='rgba(0, 0, 0, 0)'] (e.g. 'red', '#ccc') color
   * value for the background of the canvas where the cropped image is displayed.
   * @see {@link http://jcrop.org/doc/options} for further information on cropperOptions.
   *
   * @param {Image} imgData image object of the image to be cropped.
   *
   * @event ImageCropper#croppedImageLoaded triggered when cropped image is loaded.
   *
   * @returns {undefined} no return value
   *
   */
  initialize({
    imgData,
    cropperOptions = {},
    onCropperInitialized = _.noop,
    isPreviewNeeded = true,
    croppedImgWidth,
    allowedAspectRatioError = 0.01,
    logActivity = { trace: _.noop },
  } = {}) {
    this.img = imgData;
    this.croppedImgWidth = croppedImgWidth || this.img.naturalWidth;
    this.isPreviewNeeded = isPreviewNeeded;
    this.logActivity = logActivity;

    this.logActivity.trace(`Cropper: original image size ${this.img.naturalWidth}x${this.img.naturalHeight}`);

    this.allowedARError = allowedAspectRatioError;
    this.cropOptions = {
      onSelect: coords => this.onSelectionChanged(coords),

      // legacyHandlers is required when upgrading to v2.0.4 to support reading above events
      legacyHandlers: {
        onSelect: 'cropend',
        // onInitialize: 'cropinit',
      },
    };
    _.defaults(this.cropOptions, cropperOptions);

    this.onCropperInitialized = onCropperInitialized;

    this.resolveSetSelectionChangedPromise = null;
    this.setSelectionChangedPromise = new Promise((resolve) => {
      this.resolveSetSelectionChangedPromise = resolve;
    });

    this.setCropOptions();
  }

  /**
   * Get the type of the original image file.
   * @return {String} type of the original image.
   */
  getOriginalImgType() {
    const defaultImgType = 'image/jpeg'; // Default

    const startImageStringIndex = this.img.src.indexOf('image/');

    if (startImageStringIndex === -1) {
      return defaultImgType;
    }

    const skipImageStr = 'image/X'.length;

    // skip "image/X" = 7 chars
    const endImageStringIndex = this.img.src.indexOf(';', startImageStringIndex + skipImageStr);

    if (endImageStringIndex <= startImageStringIndex) {
      return defaultImgType;
    }

    return this.img.src.substring(startImageStringIndex, endImageStringIndex);
  }

  /**
   * Initialize cropper settings.
   * @return {undefined} no return value.
   */
  setCropOptions() {
    const defaultCropSettings = {
      aspectRatio: 1,
      minSize: [300, 300], // [width, height]
      maxSize: [this.img.naturalWidth, this.img.naturalHeight], // [width, height]
      bgOpacity: 0.4, // use lower opactiy to achieve same look as before
      bgColor: 'black',
      canvasBgColor: 'rgba(0, 0, 0, 0)', // transparent by default
      // below are required options to simulate same behavior as jcrop 0.9.13
      handles: ['sw', 'ne', 'nw', 'se'],
      dragbars: [],
      borders: [],
      // Exclude round from default filter.
      // When Round is applied before un-scaling, it is possible that x + w > image width
      applyFilters: ['constrain', 'extent', 'backoff', 'ratio', 'shader'],
    };

    _.defaults(this.cropOptions, defaultCropSettings);

    const imageAspectRatio = this.img.naturalWidth / this.img.naturalHeight;

    this.expectedAspectRatio = this.cropOptions.aspectRatio;
    this.logActivity.trace(`Cropper: expectedAspectRatio ${this.expectedAspectRatio}`);

    if (this.isValidAspectRatio(imageAspectRatio)) {
      this.cropOptions.aspectRatio = imageAspectRatio;
    }

    this.setCroppedImageDimensions();

    this.cropOptions.maxSize =
      (this.cropOptions.maxSize[0] === 0 || this.cropOptions.maxSize[1] === 0) ?
        [this.img.naturalWidth, this.img.naturalHeight] : this.cropOptions.maxSize;
    this.cropOptions.setSelect = this.setInitialCrop(this.cropOptions.maxSize);

    this.cropOptions.minSize = (this.cropOptions.maxSize[0] < this.cropOptions.minSize[0]
      || this.cropOptions.maxSize[1] < this.cropOptions.minSize[1]) ?
      [0, 0] : this.cropOptions.minSize;
  }

  /**
   * Sets the dimensions of the image generated after the corpping is done, taking into account the
   * dimensions constraints.
   * @return {undefined} no return value.
   */
  setCroppedImageDimensions() {
    this.croppedImgWidth = this.croppedImgWidth > this.cropOptions.maxSize[0] ?
      this.cropOptions.maxSize[0] : this.croppedImgWidth;
    this.croppedImgHeight = Math.floor(this.croppedImgWidth / this.cropOptions.aspectRatio);
    this.croppedImgHeight = (this.croppedImgHeight <= this.cropOptions.maxSize[1]
      && this.cropOptions.maxSize[1] !== 0) ?
      this.croppedImgHeight : this.cropOptions.maxSize[1];

    this.croppedImgWidth = Math.floor(this.croppedImgHeight * this.cropOptions.aspectRatio);
  }

  /**
   * Render the view.
   * @return {ImageCropper} This object.
   */
  render() {
    const self = this;

    this.$el.html(template(this));
    this.canvas = this.$('.crop-preview-canvas').get(0);

    this.context = this.canvas.getContext('2d');

    this.$('.img-crop').Jcrop(this.cropOptions, function cb() {
      const jcrop = this;

      // in new version of jcrop, initial render will not trigger onSelect event.
      // so here manually call it to simulate old behavior of resolving promise
      self.resolveSetSelectionChangedPromise();

      // workaround for known bug of scale minSize/maxSize using boxHeight
      // https://github.com/tapmodo/Jcrop/pull/152
      const { cropOptions } = self;
      const { xscale, yscale } = jcrop.opt;

      const options = _.extend({}, cropOptions.maxSize && {
        maxSize: [
          cropOptions.maxSize[0] / xscale,
          cropOptions.maxSize[1] / yscale,
        ],
      }, cropOptions.minSize && {
        minSize: [
          cropOptions.minSize[0] / xscale,
          cropOptions.minSize[1] / yscale,
        ],
      }, cropOptions.edge && {
        edge: {
          w: cropOptions.edge.w / xscale,
          e: cropOptions.edge.e / xscale,
          n: cropOptions.edge.n / yscale,
          s: cropOptions.edge.s / yscale,
        },
      });

      if (!_.isEmpty(options)) {
        jcrop.setOptions(options);

        // re-calculate selection area
        if (cropOptions.setSelect) {
          jcrop.setSelect(cropOptions.setSelect);
        }
      }

      self.onCropperInitialized(jcrop);
    });

    return this;
  }

  /**
   * Sets the initial cropped area.
   * @return {array} Coordinates for the rectangle defining the cropped area on the image.
   */
  setInitialCrop() {
    if (this.cropOptions.cropSettings) {
      this.cropSettings = {
        SourceX: this.cropOptions.cropSettings.SourceX,
        SourceY: this.cropOptions.cropSettings.SourceY,
        SourceWidth: this.cropOptions.cropSettings.SourceWidth,
        SourceHeight: this.cropOptions.cropSettings.SourceHeight,
      };

      return [
        this.cropOptions.cropSettings.SourceX,
        this.cropOptions.cropSettings.SourceY,
        this.cropOptions.cropSettings.SourceWidth,
        this.cropOptions.cropSettings.SourceHeight,
      ];
    }

    let w = this.img.naturalWidth || 0;
    let h = this.img.naturalHeight || 0;
    let x = 0;
    let y = 0;
    if (h > 0 && w > 0 && this.cropOptions.aspectRatio !== 0) {
      const imgAR = w / h;

      // If the target ratio is narrower than the original
      if (this.cropOptions.aspectRatio < imgAR) {
        // Use the same height, get the width from the original height
        w = h * this.cropOptions.aspectRatio;

        // Since we use the full height, y is 0.
        // Make sure we crop the middle section
        x = Math.floor((this.img.naturalWidth - w) / 2);
      } else {
        // Use the same width, get the height from the original width
        w = this.img.naturalWidth;
        h = w / this.cropOptions.aspectRatio;

        // We use full width, X is 0.
        // Make sure we crop the middle section
        y = Math.floor((this.img.naturalHeight - h) / 2);
      }
    }

    x = Math.round(x);
    y = Math.round(y);
    w = Math.round(w);
    h = Math.round(h);

    this.cropSettings = {
      SourceX: x,
      SourceY: y,
      SourceWidth: w,
      SourceHeight: h,
    };

    return [x, y, w, h];
  }

  /**
   * Draws the cropped area of the images to a canvas.
   * @param {array} coords coordinates of the cropped area, given by jcrop every time there is a
   * change on the cropper.
   * @return {undefined} no return value.
   */
  onSelectionChanged(coords) {
    // If there is anything invalid, just return
    const { isValid, validCoords } = this.invalidateCoords(coords);
    if (!isValid) {
      return;
    }

    const {
      x, y, w, h,
    } = validCoords;

    this.cropSettings = {
      SourceX: x,
      SourceY: y,
      SourceWidth: w,
      SourceHeight: h,
    };

    this.canvas.width = w;
    this.canvas.height = h;

    // Clear canvas
    this.context.clearRect(0, 0, this.canvas.width, this.canvas.height);

    if (this.isPreviewNeeded) {
      this.context.drawImage(
        this.img, x, y, w, h,
        0, 0, this.canvas.width, this.canvas.height
      );
    }

    this.trigger('cropSettingsUpdated', this.cropSettings);
    this.trigger('previewUpdated', this.canvas);

    this.resolveSetSelectionChangedPromise();
  }

  /**
   * Invalidate the coordinates returned by jcrop
   *
   * @private
   * @param {Object} coords - coordinate containing x, y, w, h
   *
   * @returns {Object} validationResult The validation result with updated coordiates
   * @returns {boolean} validationResult.isValid Boolean indicating coordinate is valid.
   * @returns {Object} validationResult.validCoords coordinate containing x, y, w, h
   */
  invalidateCoords(coords) {
    let {
      x, y, w, h,
    } = coords;

    let isValid = false;

    if (w === 0 || h === 0) {
      // To avoid script5022: indexsizeerror on Edge, caused by drawImage.
      return { isValid };
    }

    // When negative x or y is returned, we need to shift the selection by that much.
    if (x < 0) {
      w += x;
      x = 0;
    }

    if (y < 0) {
      h += y;
      y = 0;
    }

    // jcrop returns a floating point number that's close to the whole number ex)1200.0000001
    x = Math.round(x);
    y = Math.round(y);
    w = Math.round(w);
    h = Math.round(h);

    // jcrop is using selection width/height to compute w, h which are integer values
    // it is possible that w/h may be slightly smaller than min size. E.g. 469.123 compared to 470
    const [minw, minh] = this.cropOptions.minSize;
    const { w: minx, n: miny } = this.cropOptions.edge || {};

    if (minw > 0 && w < minw) {
      this.logActivity.trace(`Adjust w to minw: w(${w}), minw(${minw})`);

      const xshift = minw - w;

      w = minw;
      if (x > (minx || 0)) {
        this.logActivity.trace(`Adjust x by ${xshift} from ${x} (minx(${minx}))`);

        x -= xshift;
      }
    }

    if (minh > 0 && h < minh) {
      this.logActivity.trace(`Adjust h to minh: h(${h}), minh(${minh})`);

      const yshift = minh - h;

      h = minh;
      if (y > (miny || 0)) {
        this.logActivity.trace(`Adjust y by ${yshift} from ${y} (miny(${miny}))`);

        y -= yshift;
      }
    }

    if (x < 0 || y < 0) {
      // Pixel gap should be small.
      // After adjustment, there should be low chance that x/y will be negative
      // but for safety, add this check
      this.logActivity.trace('After adjustment, x or y become negative');
      return { isValid };
    }

    const selectionAspectRatio = w / h;

    if (this.expectedAspectRatio > 0 && !this.isValidAspectRatio(selectionAspectRatio)) {
      this.logActivity.trace(`Invalid ratio w(${w}) + h(${h}) expected ratio: ${this.expectedAspectRatio}`);
      return { isValid };
    }

    if (x + w > this.img.naturalWidth) {
      this.logActivity.trace(`x(${x}) + w(${w}) bigger than natural width(${this.img.naturalWidth})`);
      return { isValid };
    }

    if (y + h > this.img.naturalHeight) {
      this.logActivity.trace(`y(${y}) + h(${h}) bigger than natural height(${this.img.naturalHeight})`);
      return { isValid };
    }

    isValid = true;

    return {
      isValid,
      validCoords: {
        x,
        y,
        w,
        h,
      },
    };
  }

  /**
   * Checks if the given aspect ratio is within the permitted error limit, compared to the original
   * value for the aspect ratio.
   * @param {number} aspectRatio - aspect ratio value to be compared to this.expectedAspectRatio.
   * @return {boolean} Boolean value determining the valididty of the given aspect ratio.
   */
  isValidAspectRatio(aspectRatio) {
    return Math.abs(aspectRatio - this.expectedAspectRatio).toFixed(2) <= this.allowedARError;
  }

  /**
   * Checks if the dimensions of the cropped image are valid.
   * @param {object} img the image to check the validity.
   *
   * @return {boolean} true if the dimensions are valid, false otherwise.
   */
  validateImageSizeConstraints(img) {
    if (this.cropOptions.maxSize[1] !== 0 && img.height > this.cropOptions.maxSize[1]) {
      this.validationErrors.push('Error_ImageValidation_HeightTooLarge');
    }
    if (this.cropOptions.minSize[1] !== 0 && img.height < this.cropOptions.minSize[1]) {
      this.validationErrors.push('Error_ImageValidation_HeightTooSmall');
    }
    if (this.cropOptions.maxSize[0] !== 0 && img.width > this.cropOptions.maxSize[0]) {
      this.validationErrors.push('Error_ImageValidation_WidthTooLarge');
    }
    if (this.cropOptions.minSize[0] !== 0 && img.width < this.cropOptions.minSize[0]) {
      this.validationErrors.push('Error_ImageValidation_WidthTooSmall');
    }
  }

  onCroppedImageLoaded() {
    this.validationErrors = [];

    this.validateImageSizeConstraints(this.cropped);

    this.logActivity.trace(`Cropper: cropped image size ${this.cropped.naturalWidth}x${this.cropped.naturalHeight}`);

    if (this.validationErrors.length === 0) {
      this.trigger('croppedImageLoaded');
    } else {
      this.trigger('validationErrors', this.validationErrors);
    }
  }

  loadCroppedImage() {
    return this.setSelectionChangedPromise.then(() => {
      this.cropped = new Image();
      this.cropped.onload = () => this.onCroppedImageLoaded();

      this.logActivity.trace(`Cropper: cropped canvase size ${this.canvas.width}x${this.canvas.height}`);
      // if this.originalImgType is invalid, default image/png https://developer.mozilla.org/en-US/docs/Web/API/HTMLCanvasElement/toDataURL
      this.cropped.src = this.canvas.toDataURL(this.getOriginalImgType());
    });
  }

  /**
   * Returns an image of with the cropped area and triggers croppedImageLoaded once the data for
   * that image is loaded.
   *
   * @return {Image} image with the cropped area.
   */
  get croppedImage() {
    return this.cropped;
  }

  get fullImageWithCroppingData() {
    this.logActivity.trace(`fullImageWithCroppingData ${JSON.stringify(this.cropSettings)}`);
    return {
      fullImage: this.img,
      cropSettings: this.cropSettings,
    };
  }
}
